import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { dashboardAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ShieldAlert, Filter, Users, Building2, Clock, Zap, HardHat, Loader2, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saveProspectListContext } from '@/hooks/useReturnNavigation';

const PERIOD_OPTIONS = [
    { value: 'all', label: 'Toutes périodes' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: '7 derniers jours' },
    { value: 'month', label: 'Ce mois' },
    { value: 'quarter', label: 'Ce trimestre' },
    { value: 'year', label: 'Cette année' },
];

const isInPeriod = (dateStr, period) => {
    if (!dateStr || period === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    switch (period) {
        case 'today': return date.toDateString() === now.toDateString();
        case 'week': { const w = new Date(now); w.setDate(w.getDate() - 7); return date >= w; }
        case 'month': return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        case 'quarter': return Math.floor(date.getMonth() / 3) === Math.floor(now.getMonth() / 3) && date.getFullYear() === now.getFullYear();
        case 'year': return date.getFullYear() === now.getFullYear();
        default: return true;
    }
};

export default function DossiersARelancer() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [prospects, setProspects] = useState([]);
    const [moduleFilter, setModuleFilter] = useState('all');
    const [periodFilter, setPeriodFilter] = useState('all');

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const res = await dashboardAPI.getUnsecuredProspects();
                setProspects(res.data?.prospects || []);
            } catch (e) {
                console.error('unsecured load error', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => prospects.filter((p) => {
        if (moduleFilter !== 'all' && !(p.unsecured_modules || []).includes(moduleFilter)) return false;
        if (!isInPeriod(p.created_at, periodFilter)) return false;
        return true;
    }), [prospects, moduleFilter, periodFilter]);

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="dossiers-a-relancer-page">
                {/* En-tête */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                            <ShieldAlert className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Dossiers à relancer</h1>
                            <p className="text-sm text-slate-500 mt-0.5">
                                Dossiers actifs <strong>non sécurisés</strong> : aucune prochaine action planifiée (ni tâche, ni RDV à venir).
                            </p>
                        </div>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1.5 gap-1.5" data-testid="unsecured-count">
                        <ShieldAlert className="w-4 h-4" />
                        {loading ? '…' : filtered.length} dossier{filtered.length > 1 ? 's' : ''}
                    </Badge>
                </div>

                {/* Filtres */}
                <Card>
                    <CardContent className="py-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <Filter className="w-4 h-4 text-slate-400" />
                            <Select value={moduleFilter} onValueChange={setModuleFilter}>
                                <SelectTrigger className="w-[170px]" data-testid="module-filter">
                                    <SelectValue placeholder="Module" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les modules</SelectItem>
                                    <SelectItem value="easy_power">Courtage</SelectItem>
                                    <SelectItem value="chantier">Travaux d’optimisation</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={periodFilter} onValueChange={setPeriodFilter}>
                                <SelectTrigger className="w-[170px]" data-testid="period-filter">
                                    <SelectValue placeholder="Période" />
                                </SelectTrigger>
                                <SelectContent>
                                    {PERIOD_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Liste */}
                <Card>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-16 text-slate-400">
                                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="text-center py-16 text-slate-500" data-testid="unsecured-empty">
                                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                                <p className="font-medium text-slate-700">Aucun dossier à relancer 🎉</p>
                                <p className="text-sm text-slate-400 mt-1">Tous vos dossiers actifs ont une prochaine action planifiée.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Dossier</TableHead>
                                            <TableHead>Responsable</TableHead>
                                            <TableHead>Module(s) à relancer</TableHead>
                                            <TableHead>Sans activité</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filtered.map((p) => (
                                            <TableRow
                                                key={p.id}
                                                className="cursor-pointer hover:bg-slate-50"
                                                onClick={() => {
                                                    try {
                                                        const ids = filtered.map((x) => x.id).filter(Boolean);
                                                        saveProspectListContext(ids, p.id, {
                                                            path: '/dossiers-a-relancer',
                                                            label: 'Retour · Dossiers à relancer',
                                                        });
                                                    } catch { /* noop */ }
                                                    navigate(`/prospects/${p.id}`);
                                                }}
                                                data-testid={`unsecured-row-${p.id}`}
                                            >
                                                <TableCell>
                                                    <p className="font-medium text-slate-900">
                                                        {p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—'}
                                                    </p>
                                                    {p.company_name && (p.first_name || p.last_name) && (
                                                        <p className="text-xs text-slate-500">{p.first_name} {p.last_name}</p>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1 text-sm text-slate-700">
                                                        <Users className="w-3.5 h-3.5 text-slate-400" />
                                                        {p.dossier_apporteur_name || p.dossier_vp_name || '—'}
                                                    </div>
                                                    {p.team_name && (
                                                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                                            <Building2 className="w-3 h-3" />{p.team_name}
                                                        </p>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {(p.unsecured_modules || []).includes('easy_power') && (
                                                            <Badge variant="outline" className="text-[11px] gap-1 bg-amber-50 text-amber-700 border-amber-200">
                                                                <Zap className="w-3 h-3" />Courtage
                                                            </Badge>
                                                        )}
                                                        {(p.unsecured_modules || []).includes('chantier') && (
                                                            <Badge variant="outline" className="text-[11px] gap-1 bg-blue-50 text-blue-700 border-blue-200">
                                                                <HardHat className="w-3 h-3" />Travaux d’optimisation
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`gap-1 ${p.days_since_update > 30 ? 'bg-red-100 text-red-700' : p.days_since_update > 10 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
                                                        <Clock className="w-3 h-3" />
                                                        {p.days_since_update != null ? `${p.days_since_update} j` : '—'}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}
